import { z } from 'zod'

export const BOARD_STATUS_VALUES = ['Done', 'Working on it', 'Stuck', 'Not started'] as const
export const BOARD_PRIORITY_VALUES = ['High', 'Medium', 'Low'] as const

export const cellRefSchema = z.object({ reference: z.string(), display: z.string() })
export const cellValueSchema = z.union([z.string(), cellRefSchema])

export const boardColumnSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(['person', 'status', 'date', 'priority', 'patient', 'phone', 'text']),
})

export const boardGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
  color: z.string(),
})

export const boardSchema = z.object({
  id: z.string(),
  name: z.string(),
  columns: z.array(boardColumnSchema),
  groups: z.array(boardGroupSchema),
})

// A file attached to a note. The bytes live in a FHIR Binary uploaded by the
// caller's own Medplum client; we only store a relative `Binary/{id}` url (plus
// display metadata) so it resolves against whichever public base downloads it.
export const boardAttachmentSchema = z.object({
  // Must be a relative `Binary/{id}` reference — never an absolute/external URL.
  // The download path feeds this to the Medplum client, so accepting arbitrary
  // strings would let a direct API caller point it at an external/internal host.
  url: z
    .string()
    .trim()
    .max(2000)
    .regex(/^Binary\/[A-Za-z0-9._-]+$/, 'attachment url must be a relative Binary/{id} reference'),
  title: z.string().trim().min(1).max(300),
  contentType: z.string().trim().min(1).max(200),
  size: z.number().int().nonnegative().optional(),
})

const noteReplySchema = z.object({
  id: z.string(),
  author: cellRefSchema,
  text: z.string(),
  createdAt: z.string(),
  attachments: z.array(boardAttachmentSchema).optional(),
})

export const boardNoteSchema = noteReplySchema.extend({ replies: z.array(noteReplySchema) })

// AI-generated follow-up step; `done` is user-toggled (starts false).
export const checklistStepSchema = z.object({
  id: z.string(),
  title: z.string(),
  done: z.boolean(),
})

export const boardItemSchema = z.object({
  id: z.string(),
  boardId: z.string(),
  groupId: z.string(),
  name: z.string(),
  values: z.record(z.string(), cellValueSchema),
  notes: z.array(boardNoteSchema),
  createdAt: z.string(),
  checklist: z.array(checklistStepSchema).optional(),
  // Encounter this task was generated from; present only on AI-generated tasks.
  sourceEncounterId: z.string().optional(),
  // Date + submitting practitioner of that progress note, for provenance context.
  sourceNoteDate: z.string().optional(),
  sourcePractitioner: cellRefSchema.optional(),
  sourceDocumentReferenceId: z.string().optional(),
  // DiagnosticReport this task was generated from; present only on lab-fax tasks.
  sourceDiagnosticReportId: z.string().optional(),
  sourceLetterRequestId: z.string().optional(),
  sourceLetterTitle: z.string().optional(),
  sourceLabName: z.string().optional(),
  sourceReceivedAt: z.string().optional(),
  sourceFaxDocumentReferenceId: z.string().optional(),
  sourceFaxTaskId: z.string().optional(),
  // Present only on an identity follow-up task: its patient, for the drawer's verify panel.
  identityPatientId: z.string().optional(),
  faxActions: z.array(z.string()).optional(),
  completion: z.object({ note: z.string(), by: cellRefSchema, at: z.string() }).optional(),
  // Who completed the task and when (PD-1870); absent on tasks completed before it shipped.
  completed: z.object({ by: cellRefSchema, at: z.string() }).optional(),
  // Sent faxes recorded against this task (PD-1606). recipient/sentAt are
  // snapshots taken at link time, so a row still reads correctly after the fax
  // is deleted from RingCentral.
  outgoingFaxes: z
    .array(
      z.object({
        messageId: z.string(),
        recipient: z.string(),
        sentAt: z.string(),
        note: z.string().optional(),
        linkedBy: cellRefSchema,
        linkedAt: z.string(),
      }),
    )
    .optional(),
})
